import { useFormik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { BsPersonFill } from "react-icons/bs";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useNavigate } from "react-router-dom";
import StarRatings from "react-star-ratings";
import { toast } from "react-toastify";
import * as Yup from "yup";
import orgApi from "../../../api/org_api";
import {
  Button,
  CustomInput,
  Label,
  SearchInput,
  Table,
  Text,
} from "../../../component/common";
import { SearchableDropdown } from "../../../component/common/SearchableDropdown";
import { Style } from "../../../utils/Style";
import { cn } from "../../../utils/lib";

type InterviewerProps = { setLoading: Function };

const InterviewerManagement = ({
  setLoading,
}: InterviewerProps): JSX.Element => {
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [intType, setIntType] = useState("in-house");
  const [showAddInterviewersModal, setShowAddInterviewersModal] =
    useState(false);
  const [interviewers, setInterviewers] = useState([]);
  const [filteredInterviewers, setFilteredInterviewers] = useState([]);
  const [interviewer, setInterviewer] = useState({} as any);
  const [openInsDetailModal, setOpenInsDetailModal] = useState(false);
  const [defaultPage, setDefaultPage] = useState("profile");
  const [phErrorMsg, setPhErrorMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email Id is required")
        .email("Invalid Email"),
    }),
    validateOnBlur: true,
    onSubmit: async (values) => {
      let ph;
      if (phone && countryCode) {
        ph = phone.replace("+" + countryCode, "");
        if (ph !== "") {
        } else {
          setPhErrorMessage("Please enter the valid Phone number");
          return;
        }
      } else {
        setPhErrorMessage("Please enter the valid Phone number");
        return;
      }
      setLoading(true);
      const reqData = {
        email: values.email.toLowerCase(),
        mobile: ph!,
        role: intType,
        mobile_country_code: countryCode,
      };
      const userInfo = JSON.parse(
        localStorage.getItem("user__details")?.toString()!,
      );
      const orgId = userInfo.master_id ? userInfo.master_id : userInfo.id;
      const res = await orgApi.inviteInterviewers(orgId, reqData);
      if (res.data.success) {
        setShowAddInterviewersModal(false);
        toast("Invite sent to Interviewer");
        fetchOrgInterviewers();
      } else {
        toast(res.data.msg);
      }
      setLoading(false);
    },
  });
  const addInterviewer = () => {
    formik.resetForm();
    setIntType("in-house");
    setShowAddInterviewersModal(true);
  };
  useEffect(() => {
    fetchOrgInterviewers();
  }, [role]);

  const fetchOrgInterviewers = async () => {
    setLoading(true);
    const userInfo = JSON.parse(
      localStorage.getItem("user__details")?.toString()!,
    );
    const orgId = userInfo.master_id ? userInfo.master_id : userInfo.id;
    const res = await orgApi.fetchOrgInterviewers(orgId, { type: role });

    if (res.data.success) {
      setInterviewers(res.data.interviewer);
      setFilteredInterviewers(res.data.interviewer);
    }
    setLoading(false);
  };
  const intDetail = async (data: any) => {
    setInterviewer(data);
    setOpenInsDetailModal(true);
  };
  function handlechange(e: any) {
    setPhErrorMessage("");
    setPhone(e);

    if (e !== undefined && countryCode !== "") {
      const ph = e.replace("+" + countryCode, "");
      !ph && setPhErrorMessage("Please enter the valid Phone number");
    } else {
      setPhErrorMessage("Please enter the valid Phone number");
    }
  }
  function handleBlur(e: any) {
    setPhErrorMessage("");
    const len = e.target.value.split(" ").length;
    const c = e.target.value.split(" ");
    const cc = c[0].split("+");
    setCountryCode(cc[1]);
    const ph = e.target.value.substring(e.target.value.indexOf(" ") + 1);
    if (len > 1 && ph !== "") {
    } else {
      setPhErrorMessage("Please enter the valid Phone number");
    }
  }

  const gotoIntGrp = () => navigate("/interviewer_group");

  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  const filterInterviewers = (searchTerm: string) => {
    if (typeof searchTerm !== "string" || !searchTerm.trim()) {
      setFilteredInterviewers(interviewers);
      return;
    }

    const filtered = interviewers.filter((value: any) => {
      const name = value.candidate
        ? `${value?.candidate?.first_name} ${value?.candidate?.last_name}`.toLowerCase()
        : "";
      const email = value.email ? value.email.toLowerCase() : "";

      return (
        name.includes(searchTerm?.toLowerCase()) ||
        email.includes(searchTerm?.toLowerCase())
      );
    });

    setFilteredInterviewers(filtered);
  };

  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      filterInterviewers(searchTerm);
    }, 300),
    [interviewers],
  );

  const handleSearchChange = (e: any) => {
    const value = e;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleSearchCancel = () => {
    setSearch("");
    setFilteredInterviewers(interviewers);
  };

  const getTableDetails = () => {
    const columns = [
      { label: "Profile Pic", accessor: "profile_pic" },
      { label: "Name", accessor: "name" },
      { label: "Email", accessor: "email" },
      { label: "Contact No", accessor: "contact_no" },
      { label: "Role", accessor: "role" },
      { label: "Type", accessor: "type" },
      { label: "Designation", accessor: "designation" },
      { label: "Action", accessor: "action" },
    ];
    const data =
      filteredInterviewers &&
      filteredInterviewers.length > 0 &&
      filteredInterviewers.map((value: any) => ({
        profile_pic: (
          <>
            {!value?.candidate || !value?.candidate?.profile_pic ? (
              <div className="h-8 w-8 bg-gray-100 rounded-full">
                <div className="flex justify-center items-center h-8">
                  <BsPersonFill color="grey" size={14} />
                </div>
              </div>
            ) : (
              <div className="h-8 w-8 bg-gray-100 rounded-full">
                <div className="flex justify-center items-center h-8">
                  <img
                    src={value?.candidate?.profile_pic}
                    alt="user_profile_photo"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            )}
          </>
        ),
        name: (
          <Text
            text={
              value.candidate
                ? `${value.candidate.first_name} ${value.candidate.last_name || ""}`
                : "-"
            }
          />
        ),
        email: <Text text={value.email} />,
        contact_no: (
          <Text text={`+${value.mobile_country_code} ${value.mobile}`} />
        ),
        role: <Text text={value.role} />,
        type: (
          <Text
            text={
              !value?.candidate || !value.candidate?.interviewer
                ? "-"
                : value.candidate.interviewer.type
            }
          />
        ),
        designation: (
          <Text
            text={
              value?.candidate?.designation
                ? value?.candidate?.designation
                : "-"
            }
          />
        ),
        action: !value.candidate ? (
          <p className="p-2 text-yellow-500">Request Sent</p>
        ) : (
          <p
            className="p-2 cursor-pointer underline text-primary"
            onClick={() => intDetail(value.candidate)}
          >
            View Detail
          </p>
        ),
      }));
    return { columns, data };
  };

  return (
    <>
      {showAddInterviewersModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div
            className="fixed inset-0 w-full h-full bg-black opacity-40"
            onClick={() => setShowAddInterviewersModal(false)}
          ></div>
          <div className="flex items-center min-h-screen px-4 py-8">
            <div className="relative w-full max-w-lg p-4 mx-auto bg-white shadow-lg rounded-br-3xl">
              <div className="sm:flex">
                <div className="mt-1 text-center sm:text-left w-full">
                  <div className="flex items-center justify-between border-b">
                    <h6 className="text-base 2xl:text-lg font-bold text-gray-800 mb-2">
                      Add Interviewers
                    </h6>
                    <button
                      onClick={() => setShowAddInterviewersModal(false)}
                      type="button"
                      className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm 2xl:text-base w-8 h-8 ms-auto inline-flex justify-center items-center"
                    >
                      <svg
                        className="w-2 h-2"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 14 14"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                        />
                      </svg>
                      <span className="sr-only">Close modal</span>
                    </button>
                  </div>
                  <div className="mt-1">
                    <form onSubmit={formik.handleSubmit}>
                      <div className="grid md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-1 sm:grid-cols-1">
                        <div className="my-2 lg:mx-5 md:mx-5 xl:mx-5">
                          <div className="flex flex-row items-center justify-center mb-3 gap-5">
                            <div className="flex items-center gap-2">
                              <label
                                className={`flex items-center gap-2 cursor-pointer ${Style.label}`}
                              >
                                <input
                                  checked={intType === "in-house"}
                                  type="radio"
                                  name="default-radio"
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300"
                                  onChange={() => setIntType("in-house")}
                                  style={{
                                    accentColor: "#5051f9",
                                  }}
                                />
                                In-House
                              </label>
                            </div>

                            <div className="flex items-center gap-2">
                              <label
                                className={`flex items-center gap-2 cursor-pointer ${Style.label}`}
                              >
                                <input
                                  checked={intType === "external"}
                                  type="radio"
                                  name="default-radio"
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300"
                                  onChange={() => setIntType("external")}
                                  style={{
                                    accentColor: "#5051f9",
                                  }}
                                />
                                External
                              </label>
                            </div>
                          </div>
                          <CustomInput
                            label="Email"
                            name="email"
                            type="email"
                            required
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isError={
                              !!formik.touched.email && !!formik.errors.email
                            }
                            errorMessage={formik.errors.email}
                          />
                        </div>
                        <div className="my-2 flex flex-col gap-1.5 lg:mx-5 md:mx-5 xl:mx-5">
                          <Label text="Phone" required />
                          <PhoneInput
                            defaultCountry="IN"
                            international
                            countryCallingCodeEditable={false}
                            initialValueFormat="national"
                            name="phone"
                            value={phone}
                            onBlur={handleBlur}
                            onChange={handlechange}
                            className={cn(
                              phErrorMsg !== "" ? Style.iError : Style.iInput,
                            )}
                            autoComplete="off"
                          />
                          {phErrorMsg && (
                            <div className=" mt-0.5 text-[10px] text-red-500">
                              {phErrorMsg}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="gap-2 mt-3 sm:flex md:flex lg:flex justify-end">
                        <button
                          className="mt-2 py-1.5 px-2.5 text-gray-800 outline-none border bg-gray-100 text-xs 2xl:text-base rounded-lg"
                          onClick={() => {
                            setShowAddInterviewersModal(false);
                            setPhErrorMessage("");
                          }}
                          type="button"
                        >
                          Cancel
                        </button>
                        <button
                          className="mt-2 py-1.5 px-2.5 text-white button-primary outline-none text-xs 2xl:text-base rounded-lg"
                          type="submit"
                        >
                          Invite
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {openInsDetailModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div
            className="fixed inset-0 w-full h-full bg-black opacity-40"
            onClick={() => setOpenInsDetailModal(false)}
          ></div>
          <div className="flex items-center min-h-screen px-4 py-8">
            <div className="relative w-full max-w-3xl p-4 mx-auto bg-white shadow-lg rounded-br-3xl">
              <div className="sm:flex">
                <div className="mt-1 text-center sm:text-left w-full">
                  <div className="flex items-center justify-between border-b">
                    <h6 className="text-sm 2xl:text-lg font-bold text-gray-800 mb-2">
                      Profile
                    </h6>
                    <button
                      onClick={() => setOpenInsDetailModal(false)}
                      type="button"
                      className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm 2xl:text-lg w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                    >
                      <svg
                        className="w-2 h-2"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 14 14"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                        />
                      </svg>
                      <span className="sr-only">Close modal</span>
                    </button>
                  </div>
                  <div className="w-full mt-4 max-w-3xl">
                    <div className="grid grid-cols-4 gap-1 px-5 py-4 items-center mb-4">
                      <div className="col-span-1 flex justify-center">
                        <div
                          onClick={() => setOpenInsDetailModal(true)}
                          className="h-20 w-20 ml-4 sm:ml-3 mr-1 bg-gray-100 rounded-full overflow-hidden"
                        >
                          <img
                            src="https://randomuser.me/api/portraits/women/68.jpg"
                            alt="user_profile_photo"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="flex flex-col justify-between gap-3 h-24 text-sm 2xl:text-base">
                          <div>
                            <p className="text-gray-400 font-thin">Name</p>
                            <p className="text-gray-900 font-semibold truncate">
                              {interviewer.first_name} {interviewer.last_name}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-400 font-thin">
                              Designation
                            </p>
                            <p className="text-gray-900 font-semibold truncate">
                              {interviewer.designation}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="flex flex-col justify-between gap-3 h-24 text-sm 2xl:text-base">
                          <div>
                            <p className="text-gray-400 font-thin">Rating</p>
                            <div className="text-gray-900 font-semibold truncate">
                              <StarRatings
                                rating={interviewer?.interviewer?.avg_rating}
                                numberOfStars={5}
                                name="rating"
                                starRatedColor="orange"
                                starHoverColor="orange"
                                starDimension="14px"
                                starSpacing="2px"
                              />
                            </div>
                          </div>
                          <div>
                            <p className="text-gray-400 font-thin">
                              Experience
                            </p>
                            <p className="text-gray-900 font-semibold truncate">
                              {interviewer.year_of_experience} Years{" "}
                              {interviewer.month_of_experience} Months
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="flex flex-col justify-between gap-3 h-24">
                          <div>
                            <p className="text-gray-400 font-thin">
                              Conducted Interviews
                            </p>
                            <p className="text-gray-900 font-semibold truncate">
                              {interviewer?.interviewer?.no_of_schedule}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm 2xl:text-base font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700">
                      <ul
                        className="flex justify-start -mb-px"
                        id="default-tab"
                        data-tabs-toggle="#default-tab-content"
                        role="tablist"
                      >
                        {[
                          { label: "Profile Details", key: "profile" },
                          { label: "Skill Set", key: "skill" },
                        ].map(({ label, key }) => {
                          const isActive = defaultPage === key;
                          const baseClasses =
                            "inline-flex items-center justify-center p-2 rounded-t-lg text-sm 2xl:text-base cursor-pointer";
                          const activeClasses =
                            "border-b-4 border-blue-600 font-semibold text-gray-900";
                          const inactiveClasses =
                            "border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300";

                          return (
                            <li key={key} className="me-2">
                              <a
                                onClick={() => setDefaultPage(key)}
                                className={`${baseClasses} ${
                                  isActive ? activeClasses : inactiveClasses
                                }`}
                              >
                                {label}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    <div id="default-tab-content">
                      {defaultPage === "profile" ? (
                        <div className="flex justify-start mt-2 text-sm 2xl:text-base">
                          <div className="my-2 flex flex-col gap-2">
                            <label className={Style.label}>
                              Short Introduction
                            </label>
                            {interviewer.profile_video != null ? (
                              <video
                                width="300"
                                height="200"
                                controls
                                autoPlay={false}
                              >
                                <source
                                  src={interviewer.profile_video}
                                  type="video/mp4"
                                />
                              </video>
                            ) : (
                              <p className="font-normal text-gray-500">
                                Video not uploaded
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="">
                          {interviewer &&
                            interviewer.u_p_skill &&
                            interviewer.u_p_skill.length > 0 &&
                            interviewer.u_p_skill.map(
                              (x: any, u_index: number) => {
                                return (
                                  <div
                                    className="grid grid-cols-2 border-b py-2 px-2 gap-3"
                                    key={u_index}
                                  >
                                    <p className="text-gray-500 ">
                                      {x.skill.name}
                                    </p>
                                    <div className="">
                                      <StarRatings
                                        rating={x.self_rating}
                                        numberOfStars={5}
                                        name="rating"
                                        starRatedColor="blue"
                                        starHoverColor="blue"
                                        starDimension="18px"
                                        starSpacing="5px"
                                      />
                                    </div>
                                  </div>
                                );
                              },
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="gap-2 mt-3 sm:flex md:flex lg:flex flex-row-reverse">
                    <button
                      className="mt-2 py-1 px-2.5 text-gray-800 outline-none border text-sm 2xl:text-base rounded-lg"
                      onClick={() => setOpenInsDetailModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between">
          <div className="">
            <SearchInput
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => handleSearchChange(e)}
              onCancel={() => handleSearchCancel()}
            />
          </div>
          <div className=" h-full ">
            <div className="flex gap-3 justify-end">
              <SearchableDropdown
                options={[
                  { label: "All", value: "" },
                  { label: "In-House", value: "in-house" },
                  { label: "External", value: "external" },
                ]}
                value={role}
                onChange={(e) => setRole(e.value)}
                placeholder="Select Role"
                className="w-[200px]"
              />
              <Button
                text="+ Add"
                onClick={() => addInterviewer()}
                variant="contained"
                rounded="md"
              />
              <Button
                text="Group"
                onClick={() => gotoIntGrp()}
                variant="contained"
                rounded="md"
              />
            </div>
          </div>
        </div>
        <Table
          columns={getTableDetails().columns}
          data={getTableDetails().data}
          containerHeight="h-[calc(100vh-160px)]"
          isStickyHeader
        />
      </div>
    </>
  );
};
export default InterviewerManagement;
